/* Enhanced Media Gallery JavaScript */
/* Improves scroll snap behavior, smooth transitions, and navigation */

if (!customElements.get('enhanced-media-gallery')) {
  customElements.define('enhanced-media-gallery', class EnhancedMediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      };
      
      this.mql = window.matchMedia('(min-width: 750px)');
      this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
      this.prependMedia = this.dataset.disablePrepend != 'true';
      this.filteringOption = this.dataset.filteringOption;
      
      // Enhanced scroll snap properties
      this.isTransitioning = false;
      this.scrollThreshold = 0.25; // 25% scroll triggers next image
      this.lastScrollPosition = 0;
      this.scrollDirection = null;
      this.transitionTimeout = null;
      
      // Debounce scroll events for better performance
      this.debouncedScrollHandler = this.debounce(this.handleScrollNavigation.bind(this), 100);
      
      this.init();
    }
    
    init() {
      if (this.productInfo && Shopify.postLinksRetry) {
        this.productInfo.initShareLinks();
      }
      
      if (!this.elements.thumbnails) return;
      
      // Enhanced slide change detection
      this.elements.viewer.addEventListener('slideChanged', this.debounce(this.onSlideChanged.bind(this), 300));
      
      // Add scroll event listeners for smooth transitions
      if (this.elements.viewer.slider) {
        this.elements.viewer.slider.addEventListener('scroll', this.debouncedScrollHandler);
        this.elements.viewer.slider.addEventListener('slide', this.handleSlideNavigation.bind(this));
      }
      
      // Enhanced thumbnail click handling with proper media ID extraction
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', (e) => {
          e.preventDefault();
          // Extract media ID from data-target attribute
          const targetMediaId = mediaToSwitch.dataset.target;
          console.log('Thumbnail clicked, targeting media:', targetMediaId);
          this.setActiveMedia(targetMediaId, false);
        });
      });
      
      // Remove list semantics for desktop thumbnail layout
      if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
        this.removeListSemantic();
      }
      
      // Initialize single photo display
      this.initializeSinglePhotoDisplay();
      
      // Add keyboard navigation
      this.addKeyboardNavigation();
      
      // Enhance touch events for mobile
      this.enhanceTouchEvents();
    }
    
    /* ===== Enhanced Scroll Snap Detection ===== */
    
    handleScrollNavigation(event) {
      if (this.isTransitioning) return;
      
      const slider = this.elements.viewer.slider;
      if (!slider) return;
      
      const currentScrollPosition = slider.scrollLeft;
      const scrollPercentage = this.getScrollPercentage();
      
      // Determine scroll direction
      this.scrollDirection = currentScrollPosition > this.lastScrollPosition ? 'right' : 'left';
      this.lastScrollPosition = currentScrollPosition;
      
      // Trigger smooth transition at 25% scroll threshold
      if (scrollPercentage >= this.scrollThreshold) {
        clearTimeout(this.transitionTimeout);
        this.transitionTimeout = setTimeout(() => {
          this.smoothTransitionToNext();
        }, 150);
      }
    }
    
    getScrollPercentage() {
      const slider = this.elements.viewer.slider;
      if (!slider) return 0;
      
      const slideWidth = slider.scrollWidth / this.getMediaCount();
      const currentPosition = slider.scrollLeft;
      const currentSlideIndex = Math.floor(currentPosition / slideWidth);
      const positionInSlide = (currentPosition % slideWidth) / slideWidth;
      
      return positionInSlide;
    }
    
    smoothTransitionToNext() {
      if (this.isTransitioning) return;
      
      this.isTransitioning = true;
      const currentActiveMedia = this.elements.viewer.querySelector('.product__media-item.is-active');
      
      if (!currentActiveMedia) {
        this.isTransitioning = false;
        return;
      }
      
      let targetMedia = null;
      
      if (this.scrollDirection === 'right') {
        targetMedia = currentActiveMedia.nextElementSibling;
      } else if (this.scrollDirection === 'left') {
        targetMedia = currentActiveMedia.previousElementSibling;
      }
      
      if (targetMedia && targetMedia.classList.contains('product__media-item')) {
        const mediaId = targetMedia.dataset.mediaId;
        this.setActiveMedia(mediaId, false);
        
        // Smooth scroll to target with enhanced easing
        this.smoothScrollToMedia(targetMedia);
      }
      
      // Reset transition flag after animation
      setTimeout(() => {
        this.isTransitioning = false;
      }, 400);
    }
    
    smoothScrollToMedia(targetMedia) {
      if (!targetMedia || !this.elements.viewer.slider) return;
      
      const slider = this.elements.viewer.slider;
      const targetPosition = targetMedia.offsetLeft;
      
      // Use native smooth scrolling with enhanced behavior
      slider.scrollTo({
        left: targetPosition,
        behavior: 'smooth'
      });
    }
    
    /* ===== Enhanced Media Management ===== */
    
    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }
    
    setActiveMedia(mediaId, prepend, filtering = false, currentVariant = null) {
      // Handle filtering if needed
      if (filtering && currentVariant) {
        const allSlides = this.querySelectorAll('.product__media-item, .thumbnail-list__item, .slider-counter__link');
        allSlides.forEach(slide => {
          if (slide.dataset.alt === currentVariant[this.filteringOption] || slide.dataset.alt === 'always_display') {
            slide.classList.remove('hidden');
          } else {
            slide.classList.add('hidden');
          }
        });
      }
      
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      
      // Enhanced active state management with smooth transitions
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
        element.style.opacity = '0.9';
      });
      
      if (activeMedia) {
        activeMedia.classList.add('is-active');
        activeMedia.style.opacity = '1';
        
        // Smooth transition effect
        activeMedia.style.transform = 'scale(1.0)';
        activeMedia.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
      }
      
      // Handle prepending if enabled
      if (prepend && this.prependMedia) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }
      
      this.preventStickyHeader();
      
      // Enhanced scroll behavior with timing optimization
      setTimeout(() => {
        if (this.elements.thumbnails) {
          this.smoothScrollToMedia(activeMedia);
        }
        
        // Only auto-scroll on desktop to prevent mobile jumping
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          if (window.innerWidth >= 1024) {
            activeMedia.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'center'
            });
          }
        }
      }, 100);
      
      this.playActiveMedia(activeMedia);
      
      // Handle filtering updates
      if (filtering && currentVariant) {
        if (this.elements.viewer && this.elements.viewer.initPages) this.elements.viewer.initPages();
        if (this.elements.viewer && this.elements.viewer.update) this.elements.viewer.update();
      }
      
      if (!this.elements.thumbnails) return;
      
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }
    
    /* ===== Enhanced Thumbnail Management ===== */
    
  setActiveThumbnail(thumbnail) {
    if (!this.elements.thumbnails || !thumbnail) return;
    
    // Enhanced thumbnail active state with smooth transitions - without border
    this.elements.thumbnails.querySelectorAll('button').forEach((element) => {
      element.removeAttribute('aria-current');
      element.style.opacity = '0.7';
      element.style.transform = 'scale(1)';
      // Remove any potential border
      element.style.border = 'none';
      element.style.outline = 'none';
    });
    
    const button = thumbnail.querySelector('button');
    button.setAttribute('aria-current', true);
    button.style.opacity = '1';
    button.style.transform = 'scale(1.05)';
    button.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
    // Ensure no border on active state
    button.style.border = 'none';
    button.style.outline = 'none';
      
      // Enhanced visibility check and smooth scrolling
      if (this.elements.thumbnails.isSlideVisible && 
          this.elements.thumbnails.isSlideVisible(thumbnail, false, 10, true)) return;
      
      // Smooth thumbnail scrolling
      if (this.elements.thumbnails.vertical) {
        this.elements.thumbnails.slider.scrollTo({ 
          top: thumbnail.offsetTop,
          behavior: 'smooth'
        });
      } else {
        this.elements.thumbnails.slider.scrollTo({ 
          left: thumbnail.offsetLeft,
          behavior: 'smooth'
        });
      }
    }
    
    /* ===== Enhanced Touch and Keyboard Support ===== */
    
    enhanceTouchEvents() {
      if (!this.elements.viewer.slider) return;
      
      let touchStartX = null;
      let touchStartY = null;
      
      this.elements.viewer.slider.addEventListener('touchstart', (e) => {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
      }, { passive: true });
      
      this.elements.viewer.slider.addEventListener('touchmove', (e) => {
        if (!touchStartX || !touchStartY) return;
        
        const touchEndX = e.touches[0].clientX;
        const touchEndY = e.touches[0].clientY;
        
        const deltaX = touchStartX - touchEndX;
        const deltaY = touchStartY - touchEndY;
        
        // Horizontal swipe detection with enhanced sensitivity
        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
          const currentActive = this.elements.viewer.querySelector('.product__media-item.is-active');
          if (!currentActive) return;
          
          let targetMedia = null;
          
          if (deltaX > 0) { // Swipe left (next)
            targetMedia = currentActive.nextElementSibling;
          } else { // Swipe right (previous)
            targetMedia = currentActive.previousElementSibling;
          }
          
          if (targetMedia && targetMedia.classList.contains('product__media-item')) {
            const mediaId = targetMedia.dataset.mediaId;
            this.setActiveMedia(mediaId, false);
          }
        }
      }, { passive: true });
    }
    
    addKeyboardNavigation() {
      document.addEventListener('keydown', (e) => {
        if (!this.isVisible()) return;
        
        const currentActive = this.elements.viewer.querySelector('.product__media-item.is-active');
        if (!currentActive) return;
        
        let targetMedia = null;
        
        switch(e.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            e.preventDefault();
            targetMedia = currentActive.nextElementSibling;
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            e.preventDefault();
            targetMedia = currentActive.previousElementSibling;
            break;
        }
        
        if (targetMedia && targetMedia.classList.contains('product__media-item')) {
          const mediaId = targetMedia.dataset.mediaId;
          this.setActiveMedia(mediaId, false);
        }
      });
    }
    
    /* ===== Utility Methods ===== */
    
    getMediaCount() {
      return this.elements.viewer.querySelectorAll('.product__media-item').length;
    }
    
    isVisible() {
      return this.offsetWidth > 0 && this.offsetHeight > 0;
    }
    
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }
    
    /* ===== Preserved Original Methods ===== */
    
    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }
    
    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }
    
    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }
    
    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
    
    initializeSinglePhotoDisplay() {
      const allMediaItems = this.elements.viewer.querySelectorAll('[data-media-id]');
      let activeItem = this.elements.viewer.querySelector('[data-media-id].is-active');
      
      if (!activeItem && allMediaItems.length > 0) {
        activeItem = allMediaItems[0];
        activeItem.classList.add('is-active');
      }
      
      allMediaItems.forEach(item => {
        if (item !== activeItem) {
          item.classList.remove('is-active');
          item.style.opacity = '0.9';
        } else {
          item.style.opacity = '1';
        }
      });
    }
  });
}

/* ===== Initialize Enhanced Gallery ===== */
// Work with existing Shopify media-gallery instead of replacing it
document.addEventListener('DOMContentLoaded', function() {
  // Add a small delay to ensure Shopify's gallery is fully initialized
  setTimeout(function() {
    const existingGalleries = document.querySelectorAll('media-gallery');
    
    existingGalleries.forEach(gallery => {
      if (!gallery.classList.contains('enhanced-initialized')) {
        gallery.classList.add('enhanced-initialized');
        
        console.log('Enhancing gallery:', gallery);
        
        // Find thumbnail container and main gallery
        const thumbnailContainer = gallery.querySelector('[id^="GalleryThumbnails"]');
        const mainGallery = gallery.querySelector('[id^="GalleryViewer"]');
        
        if (thumbnailContainer && mainGallery) {
          // Add click handlers to each thumbnail
          const thumbnails = thumbnailContainer.querySelectorAll('[data-target]');
          
          thumbnails.forEach((thumbnail) => {
            const button = thumbnail.querySelector('button');
            if (button) {
              // Add our enhanced click handler
              button.addEventListener('click', function(e) {
                console.log('Thumbnail clicked!');
                
                // Get the target media ID
                const targetId = thumbnail.dataset.target;
                console.log('Target ID:', targetId);
                
                // Find the corresponding media item
                const targetMedia = mainGallery.querySelector(`[data-media-id="${targetId}"]`);
                console.log('Target media:', targetMedia);
                
                if (targetMedia) {
                  // Remove active class from all media items
                  mainGallery.querySelectorAll('[data-media-id]').forEach((item) => {
                    item.classList.remove('is-active');
                  });
                  
                  // Add active class to target
                  targetMedia.classList.add('is-active');
                  
                  // Update thumbnail active states - remove border
                  thumbnailContainer.querySelectorAll('button').forEach((btn) => {
                    btn.removeAttribute('aria-current');
                    btn.classList.remove('active');
                    // Remove any potential border
                    btn.style.border = 'none';
                    btn.style.outline = 'none';
                  });
                  
                  button.setAttribute('aria-current', 'true');
                  button.classList.add('active');
                  // Ensure no border on active state
                  button.style.border = 'none';
                  button.style.outline = 'none';
                  
                  // Scroll to the target media
                  const slider = mainGallery.querySelector('.product__media-list');
                  if (slider) {
                    // Calculate the position to center the image
                    const containerWidth = slider.clientWidth;
                    const imageWidth = targetMedia.offsetWidth;
                    const targetPosition = targetMedia.offsetLeft - (containerWidth / 2) + (imageWidth / 2);
                    
                    slider.scrollTo({
                      left: Math.max(0, targetPosition),
                      behavior: 'smooth'
                    });
                  }
                }
              });
            }
          });
          
          // Add scroll-based thumbnail update functionality
          const slider = mainGallery.querySelector('.product__media-list');
          if (slider) {
            let scrollTimeout;
            
            slider.addEventListener('scroll', function() {
              clearTimeout(scrollTimeout);
              
              // Reduced debounce for faster response (was 100ms, now 50ms)
              scrollTimeout = setTimeout(() => {
                const mediaItems = mainGallery.querySelectorAll('[data-media-id]');
                const sliderRect = slider.getBoundingClientRect();
                const sliderCenter = sliderRect.left + sliderRect.width / 2;
                
                let activeMediaId = null;
                let closestDistance = Infinity;
                
                // Find which media item is closest to the center of the viewport
                mediaItems.forEach((mediaItem) => {
                  const mediaRect = mediaItem.getBoundingClientRect();
                  const mediaCenter = mediaRect.left + mediaRect.width / 2;
                  const distance = Math.abs(sliderCenter - mediaCenter);
                  
                  if (distance < closestDistance) {
                    closestDistance = distance;
                    activeMediaId = mediaItem.dataset.mediaId;
                  }
                });
                
                // Update active states if we found a media item
                if (activeMediaId) {
                  // Update media items
                  mediaItems.forEach((item) => {
                    if (item.dataset.mediaId === activeMediaId) {
                      item.classList.add('is-active');
                    } else {
                      item.classList.remove('is-active');
                    }
                  });
                  
                  // Update thumbnails
                  const targetThumbnail = thumbnailContainer.querySelector(`[data-target="${activeMediaId}"]`);
                  if (targetThumbnail) {
                    thumbnailContainer.querySelectorAll('button').forEach((btn) => {
                      btn.removeAttribute('aria-current');
                      btn.classList.remove('active');
                    });
                    
                    const targetButton = targetThumbnail.querySelector('button');
                    if (targetButton) {
                      targetButton.setAttribute('aria-current', 'true');
                      targetButton.classList.add('active');
                      
                      // Auto-scroll thumbnail container to show the active thumbnail
                      const thumbnailSlider = thumbnailContainer.querySelector('.thumbnail-slider, .slider');
                      if (thumbnailSlider) {
                        const thumbnailRect = targetThumbnail.getBoundingClientRect();
                        const containerRect = thumbnailSlider.getBoundingClientRect();
                        
                        // Check if thumbnail is fully visible
                        const isVisible = (
                          thumbnailRect.left >= containerRect.left &&
                          thumbnailRect.right <= containerRect.right
                        );
                        
                        if (!isVisible) {
                          // Calculate position to center the thumbnail in its container
                          const thumbnailCenter = targetThumbnail.offsetLeft + (targetThumbnail.offsetWidth / 2);
                          const containerCenter = thumbnailSlider.offsetWidth / 2;
                          const scrollPosition = thumbnailCenter - containerCenter;
                          
                          thumbnailSlider.scrollTo({
                            left: Math.max(0, scrollPosition),
                            behavior: 'smooth'
                          });
                        }
                      }
                    }
                  }
                }
              }, 25); // Reduced to 25ms for ultra-fast response
            });
          }
          
          // Initialize the first thumbnail as active
          const firstThumbnail = thumbnailContainer.querySelector('[data-target] button');
          const firstMedia = mainGallery.querySelector('[data-media-id]');
          
          if (firstThumbnail && firstMedia) {
            // Remove any existing active states
            mainGallery.querySelectorAll('[data-media-id]').forEach((item) => {
              item.classList.remove('is-active');
            });
            
            thumbnailContainer.querySelectorAll('button').forEach((btn) => {
              btn.removeAttribute('aria-current');
              btn.classList.remove('active');
            });
            
            // Set first items as active
            firstMedia.classList.add('is-active');
            firstThumbnail.setAttribute('aria-current', 'true');
            firstThumbnail.classList.add('active');
          }
        }
      }
    });
  }, 500); // Small delay to ensure Shopify gallery is ready
});

/* ===== CSS Scroll Snap Polyfill for Older Browsers ===== */
(function() {
  // Simple scroll snap polyfill for browsers that don't support it
  if (!CSS.supports('scroll-snap-type', 'x mandatory')) {
    const galleries = document.querySelectorAll('.product__media-list.slider');
    
    galleries.forEach(gallery => {
      let scrollTimeout;
      
      gallery.addEventListener('scroll', function() {
        clearTimeout(scrollTimeout);
        
        scrollTimeout = setTimeout(() => {
          const slides = gallery.querySelectorAll('.product__media-item');
          const galleryWidth = gallery.offsetWidth;
          const scrollLeft = gallery.scrollLeft;
          
          let closestSlide = slides[0];
          let closestDistance = Math.abs(scrollLeft);
          
          slides.forEach((slide, index) => {
            const slideLeft = slide.offsetLeft;
            const distance = Math.abs(scrollLeft - slideLeft);
            
            if (distance < closestDistance) {
              closestDistance = distance;
              closestSlide = slide;
            }
          });
          
          gallery.scrollTo({
            left: closestSlide.offsetLeft,
            behavior: 'smooth'
          });
        }, 150);
      });
    });
  }
})();
